import { useEffect, useRef, useState } from 'react';
import type { CurrencyCode } from '@bbpos/types';
import { formatCurrency } from '@/lib/format';

interface Props {
  terminalName: string;
  amount: number;            // minor units (paise for INR)
  /** Tenant currency. Optional; defaults to INR so existing callers compile
   *  unchanged and INR output stays byte-identical, while non-INR tenants
   *  render the correct symbol/decimals. */
  currency?: CurrencyCode;
  timeoutSeconds?: number;
  onCancel: () => void;
  // Caller is responsible for polling and calling one of these.
  status: 'WAITING_FOR_PAYMENT' | 'SUCCESS' | 'DECLINED' | 'TIMEOUT' | 'ERROR' | 'CANCELLED';
  transactionReference?: string | null;
}

/**
 * Visual shell for a physical-terminal (EDC) payment push.
 *
 * The backend push/callback lifecycle is driven by the existing
 * POST /payments/terminal/push endpoint. This component is the cashier-facing
 * surface: a countdown, a status banner, and a cancel affordance.
 *
 * The caller wires the `status` prop from a polling loop (e.g. TanStack
 * Query with refetchInterval: 2_000 against a bill-status endpoint).
 */
export function TerminalPaymentDialog({
  terminalName, amount, currency = 'INR', timeoutSeconds = 120, status, transactionReference, onCancel,
}: Props) {
  const [remaining, setRemaining] = useState(timeoutSeconds);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (status !== 'WAITING_FOR_PAYMENT') return;
    const id = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startRef.current) / 1000);
      setRemaining(Math.max(0, timeoutSeconds - elapsed));
    }, 500);
    return () => window.clearInterval(id);
  }, [status, timeoutSeconds]);

  const percent = Math.max(0, Math.min(100, (remaining / timeoutSeconds) * 100));
  const banner = resolveBanner(status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/60 backdrop-blur-sm animate-fade-in p-4">
      <div className="w-full max-w-md bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl border border-neutral-100 dark:border-neutral-700 overflow-hidden animate-scale-in">
        <div className="bg-gradient-to-r from-violet-600 to-pink-600 p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center text-xl" aria-hidden>
              💳
            </div>
            <div>
              <h2 className="text-lg font-semibold">Card / UPI Terminal</h2>
              <p className="text-xs text-violet-100">{terminalName}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-neutral-600 dark:text-neutral-300">Amount on device</span>
            <span className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">
              {formatCurrency(amount, currency)}
            </span>
          </div>

          <CircularCountdown remaining={remaining} total={timeoutSeconds} percent={percent} />

          <div className={`rounded-xl border p-3 text-sm ${banner.classes}`}>
            <div className="font-medium">{banner.label}</div>
            {transactionReference && (
              <div className="text-xs mt-1 font-mono opacity-80 tabular-nums">Ref: {transactionReference}</div>
            )}
          </div>

          {status === 'WAITING_FOR_PAYMENT' && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full py-3 rounded-xl border border-neutral-200 dark:border-neutral-700 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              Cancel on terminal
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CircularCountdown({ remaining, total, percent }: { remaining: number; total: number; percent: number }) {
  const R = 44;
  const C = 2 * Math.PI * R;
  const dash = (percent / 100) * C;
  return (
    <div className="flex items-center justify-center py-2">
      <div className="relative w-28 h-28">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r={R} className="stroke-neutral-200 dark:stroke-neutral-700" strokeWidth="8" fill="none" />
          <circle
            cx="50" cy="50" r={R}
            className="stroke-violet-500 transition-[stroke-dashoffset] duration-500 ease-linear"
            strokeWidth="8" fill="none"
            strokeDasharray={C}
            strokeDashoffset={C - dash}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">{remaining}s</span>
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400">of {total}s</span>
        </div>
      </div>
    </div>
  );
}

function resolveBanner(status: Props['status']): { label: string; classes: string } {
  switch (status) {
    case 'WAITING_FOR_PAYMENT':
      return {
        label: 'Waiting for payment confirmation… the customer pays on the terminal',
        classes: 'bg-warning-50 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-300 dark:border-warning-700/60',
      };
    case 'SUCCESS':
      return {
        label: 'Payment captured',
        classes: 'bg-success-50 text-success-700 border-success-200 dark:bg-success-900/30 dark:text-success-300 dark:border-success-700/60',
      };
    case 'DECLINED':
      return {
        label: 'Card declined — ask customer for another method',
        classes: 'bg-danger-50 text-danger-700 border-danger-200 dark:bg-danger-900/30 dark:text-danger-300 dark:border-danger-700/60',
      };
    case 'TIMEOUT':
      return {
        label: 'Terminal timed out — tap Retry or switch to CASH',
        classes: 'bg-warning-50 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-300 dark:border-warning-700/60',
      };
    case 'ERROR':
      return {
        label: 'Terminal error — check device and retry',
        classes: 'bg-danger-50 text-danger-700 border-danger-200 dark:bg-danger-900/30 dark:text-danger-300 dark:border-danger-700/60',
      };
    case 'CANCELLED':
      return {
        label: 'Payment cancelled',
        classes: 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700',
      };
  }
}
